import { forwardRef, useEffect, useState } from 'react';

import { FormikErrors, FormikTouched } from 'formik';

import dayjs from 'dayjs';

import styles from './contract-line-customer-header.module.scss';
import ContractDropdownHeaderItem from '@/components/common/ContractInputHeaderItem/ContractDropdownHeaderItem';
import { PurchaseOrderOutputType, PurchaseOrderStatusEnum } from '@/graphql/contracts/_generated_/contracts';
import { PaginatedFilteredPurchaseOrders } from '@/pages/ContractDetails/ContractLines/hooks/useGetPurchaseOrders';

import ContractHeaderItem from '@/components/common/ContractHeaderItem/ContractHeaderInputItem';
import { NumberUtils } from '@/utils/numbers.utils';
import { DEFAULT_DATE_PATTERN } from '@/config/common.config';
import { isValidDate } from '@/utils/date.utils';
import AddPurchaseOrderModal
  from '../../../AssignPurchaseOrderForLines/components/AddPurchaseOrderModal/AddPurchaseOrderModal';

export interface IPurchaseOrderColumnProps {
  fieldName: string;
  fieldKey: string;
  isEditable: boolean;
  isRequired: boolean;
  touched: FormikTouched<any>;
  errors: FormikErrors<any>;
  setFieldTouched: any;
  setFieldValue: any;
  searchPos: {
    getPurchaseOrdersItems: (
      params: PaginatedFilteredPurchaseOrders
    ) => Promise<(({
      id?: string | null;
      customerId?: string | null;
      customerName?: string | null;
      customerCode?: string | null;
      poNumber?: string | null;
      poDate?: any | null;
      poStartDate?: any | null;
      poEndDate?: any | null;
      poTotalValue?: string | null;
      poStatus?: PurchaseOrderStatusEnum | null;
      poNote?: string | null;

    } & {
    }) | null)[]>,

    getPurchaseOrdersLoading: boolean;
  };
  isLoadingContractDetails?: boolean;
  customerCode: string | undefined;
  currency?: string | undefined;
  customerName?: string | undefined;
  customerId?: string | undefined;
}

const PurchaseOrderColumn = forwardRef<any, IPurchaseOrderColumnProps>(
  (props, ref) => {
    const {
      fieldName,
      fieldKey,
      isEditable,
      isRequired,
      touched,
      errors,
      setFieldTouched,
      setFieldValue,
      searchPos,
      customerCode,
      currency,
      customerName,
      customerId,
    } = props;

    const [purchaseOrders, setPurchaseOrders] = useState<
    (PurchaseOrderOutputType | undefined | null)[]
    >([]);
    const [typedPoNumber, setTypedPoNumber] = useState('');
    const [isAddPurchaseOrderModalOpen, setIsAddPurchaseOrderModalOpen] = useState(false);
    const [currentPurchaseOrder, setCurrentPurchaseOrder] = useState<
    PurchaseOrderOutputType | undefined | null
    >(undefined);

    const dropdownRef = ref as React.MutableRefObject<any>;

    const formatDate = (poDate?: any) => {
      return isValidDate(poDate)
        ? dayjs(poDate).format(DEFAULT_DATE_PATTERN)
        : '';
    };

    const fetchAndSetPurchaseOrders = async (code?: string) => {
      if (!code) {
        setPurchaseOrders([]);
        return;
      }
      const res = await searchPos.getPurchaseOrdersItems({
        filter: {
          customerCode: { eq: code },
          poCurrency: { eq: currency },
        },
        sort: [
          {
            field: 'Created',
            order: 'Descending',
          },
        ],
      });
      setPurchaseOrders(res ?? []);
    };

    useEffect(() => {
      dropdownRef?.current?.setValue(undefined);
      setCurrentPurchaseOrder(undefined);
      fetchAndSetPurchaseOrders(customerCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerCode]);

    const createNewPoOption = (searchString: string): PurchaseOrderOutputType => ({
      id: 'create-new-po',
      poNumber: `Create new PO: ${searchString}`,
      isCustomOption: true,
      typedValue: searchString
    } as any);

    const handleInputChange = (value: string) => {
      setTypedPoNumber(value);
    };

    const handleChange = (option: PurchaseOrderOutputType | undefined | null) => {
      if (!option) {
        setCurrentPurchaseOrder(undefined);
        setTypedPoNumber('');
        fetchAndSetPurchaseOrders(customerCode);
        return;
      }

      if (option && (option as any).isCustomOption) {
        setIsAddPurchaseOrderModalOpen(true);
        return;
      }

      const value = option?.id;
      setCurrentPurchaseOrder(option);
      setFieldTouched(fieldKey);
      setFieldValue(fieldKey, value);
    };


    function handleAddPurchaseOrderModalClose(): void {
      setTypedPoNumber('');
      setIsAddPurchaseOrderModalOpen(false);
    }

    const handleAddPurchaseOrderModalSuccess = (createdPurchaseOrder: any) => {
      handleChange(createdPurchaseOrder);
      return createdPurchaseOrder;
    };

    return (
      <>
        <div className={styles['party-column']}>
          <div className={styles['party-column-header']}>
            <span>{fieldName}</span>
          </div>
          <hr style={{ marginBottom: '10px' }} />
          <div>
            <div className={styles['dropdown-margin']}>
              <ContractDropdownHeaderItem<
              PurchaseOrderOutputType | undefined | null
              >
                ref={ref}
                required={isRequired}
                label="Number"
                id={fieldKey}
                defaultValue={undefined}
                placeholder={'Select...'}
                getOptionLabel={(option) => option?.poNumber ?? ''}
                getOptionValue={(option) => option?.id ?? ''}
                optionsData={
                  customerCode
                    ? (typedPoNumber.length > 0
                      ? [...purchaseOrders.filter(po =>
                        po?.poNumber?.toLowerCase().includes(typedPoNumber.toLowerCase())
                      ), createNewPoOption(typedPoNumber)]
                      : purchaseOrders)
                    : []
                }
                selectedOption={currentPurchaseOrder ?? undefined}
                handleInputChange={handleInputChange}
                handleChange={handleChange}
                isLoading={searchPos?.getPurchaseOrdersLoading}
                errorMessage={
                  touched[fieldKey] && errors[fieldKey] ? errors[fieldKey] : false
                }
                isSearchable={true}
                disabled={!isEditable}
                isClearable={true}
              />
            </div>
          </div>
          <div className={styles['contract-header-container']}>
            <ContractHeaderItem
              label="Start Date"
              value={formatDate(currentPurchaseOrder?.poStartDate)}
              disabled
              inputStyle={{ width: '100%' }}
            />
          </div>
          <div className={styles['contract-header-container']}>
            <ContractHeaderItem
              label="End Date"
              value={formatDate(currentPurchaseOrder?.poEndDate)}
              disabled
              inputStyle={{ width: '100%' }}
            />
          </div>
          <div className={styles['contract-header-container']}>
            <ContractHeaderItem
              label="Total Value"
              value={NumberUtils.prettifyWithRound(currentPurchaseOrder?.poTotalValue) ?? ''}
              disabled
              inputStyle={{ width: '100%', textAlign: 'right' }}
            />
          </div>
        </div>

        <AddPurchaseOrderModal
          isOpen={isAddPurchaseOrderModalOpen}
          onClose={handleAddPurchaseOrderModalClose}
          onSuccess={handleAddPurchaseOrderModalSuccess}
          initialPoNumber={typedPoNumber}
          customerId={customerId}
          customerCode={customerCode}
          customerName={customerName}
          currency={currency}
        />
      </>
    );
  });

export default PurchaseOrderColumn;
